import React, { useCallback } from 'react'

import { Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { formatLongDate } from 'Utils/FormatDate'
import { EMPTY_OBJECT, NOOP } from 'Utils/stableDefaults'

import CancelStep from './CancelStep'
import ConsequencesList from './ConsequencesList'
import PauseOffer from './PauseOffer'
import ReasonSelect from './ReasonSelect'
import ReasonStep from './ReasonStep'
import SaveOffer from './SaveOffer'
import useCancelModal, { CANCEL_MODAL_STEP } from './useCancelModal'
import { PLAN_TYPE, TIER } from '../../constants'
import { planChangesShape, trackingDataShape } from '../../propTypes'

function CancelSubscriptionModal({
  isOpen,
  onClose,
  onSubmit,
  onReasonCapture = null,
  subscriptionId,
  trackingData = EMPTY_OBJECT,
  feeData = null,
  feeError = null,
  isFeeLoading = false,
  locale = 'en',
  subscriptionV16Enabled = false,
  planType = 'yearly',
  gracePeriodEligible = true,
  commitmentReleased = false,
  showPauseOffer = false,
  oneMonthPause = false,
  onPause = null,
  isPauseLoading = false,
  tier = TIER.ESSENTIALS,
  periodEndDate = null,
  isFeeFreeFlow = false,
  plansRestructureEnabled = false,
  cancelEndsNow = false,
  accessUntilDate = null,
  billingTerm = PLAN_TYPE.MONTHLY,
  planChanges = null,
  essentialsPrice = null,
  currentPrice = null,
  premiumModelCount = null,
  premiumGenerations = null,
  downloadedCount = null,
  downloadedValue = null,
  upgradeUrl = '',
  onSchedule = NOOP,
  isScheduling = false,
}) {
  const {
    step,
    handleContinueToCancel,
    handlePauseClick,
    handleContinueFromReason,
    handleBackFromReason,
    handleBackToReason,
    isContinueFromReasonDisabled,
    hasReasonStep,
    feeBreakdown,
    hasFee,
    showInlineReasonSelect,
    accessUntilFormatted,
    isAcknowledged,
    setIsAcknowledged,
    selectedReason,
    isOtherSelected,
    otherText,
    handleReasonChange,
    handleOtherTextChange,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    isSubmitting,
    maxCustomReasonLength,
  } = useCancelModal({
    isOpen,
    onClose,
    onSubmit,
    onReasonCapture,
    subscriptionId,
    trackingData,
    feeData,
    feeError,
    isFeeLoading,
    locale,
    subscriptionV16Enabled,
    planType,
    showPauseOffer,
    onPause,
    isPauseLoading,
    periodEndDate,
    accessUntilDate,
    tier,
  })

  const translate = useCallback(
    (key) => I18n.t(
      `subscription_management.cancel_modal.${key}`,
      { ns: 'rails', lng: locale }
    ),
    [locale]
  )

  const showAccessUntilNotice = Boolean(!isFeeFreeFlow && !hasFee && !feeError && accessUntilFormatted)

  const handleDowngrade = useCallback(async (target) => {
    if (await onSchedule(target)) handleClose()
  }, [onSchedule, handleClose])

  const reasonSelect = (
    <ReasonSelect
      selectedReason={selectedReason}
      isOtherSelected={isOtherSelected}
      otherText={otherText}
      onReasonChange={handleReasonChange}
      onOtherTextChange={handleOtherTextChange}
      maxCustomReasonLength={maxCustomReasonLength}
      locale={locale}
      tier={tier}
      isFeeFreeFlow={isFeeFreeFlow}
    />
  )

  const saveOffer = isFeeFreeFlow ? (
    <SaveOffer
      reason={selectedReason?.value}
      tier={tier}
      billingTerm={billingTerm}
      planChanges={planChanges}
      essentialsPrice={essentialsPrice}
      currentPrice={currentPrice}
      premiumModelCount={premiumModelCount}
      premiumGenerations={premiumGenerations}
      effectiveDate={formatLongDate(planChanges?.effectiveAt, locale, 'UTC')}
      downloadedCount={downloadedCount}
      downloadedValue={downloadedValue}
      upgradeUrl={upgradeUrl}
      isScheduling={isScheduling}
      onDowngrade={handleDowngrade}
      onKeep={handleClose}
      locale={locale}
    />
  ) : null

  if (step === CANCEL_MODAL_STEP.REASON) {
    return (
      <ReasonStep
        isOpen={isOpen}
        onClose={handleClose}
        onBack={handleBackFromReason}
        onContinue={handleContinueFromReason}
        isContinueDisabled={isContinueFromReasonDisabled}
        showBackButton={showPauseOffer}
        showAccessUntilNotice={showAccessUntilNotice}
        accessUntilFormatted={accessUntilFormatted}
        reasonSelect={reasonSelect}
        saveOffer={saveOffer}
        isFeeFreeFlow={isFeeFreeFlow}
        locale={locale}
        tier={tier}
      />
    )
  }

  if (step === CANCEL_MODAL_STEP.PAUSE_OFFER) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        header={translate('title')}
        width="small"
        canClose
        // Triangle Modal closes its internal state before calling onClose, so a close attempt
        // during an in-flight pause must be blocked here — a no-op onClose would leave the
        // modal hidden while the parent still considers it open.
        disableClose={isPauseLoading}
        customClass={`cancel-subscription-modal cancel-subscription-modal--pause-offer${
          isFeeFreeFlow ? ' cancel-subscription-modal--fee-free' : ''}`}
      >
        <PauseOffer
          onPause={handlePauseClick}
          onContinueToCancel={handleContinueToCancel}
          isPauseLoading={isPauseLoading}
          oneMonth={oneMonthPause}
          locale={locale}
        />
      </Modal>
    )
  }

  return (
    <CancelStep
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      onBack={handleBackToReason}
      hasReasonStep={hasReasonStep}
      hasFee={hasFee}
      hasFeeError={Boolean(feeError)}
      feeBreakdown={feeBreakdown}
      accessUntilFormatted={accessUntilFormatted}
      showAccessUntilNotice={showAccessUntilNotice}
      isAcknowledged={isAcknowledged}
      onAcknowledgeChange={setIsAcknowledged}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitting={isSubmitting}
      consequencesList={hasReasonStep ? (
        <ConsequencesList
          hasFee={hasFee}
          planType={planType}
          gracePeriodEligible={gracePeriodEligible}
          commitmentReleased={commitmentReleased}
          isFeeFreeFlow={isFeeFreeFlow}
          cancelEndsNow={cancelEndsNow}
          isAnnual={billingTerm === PLAN_TYPE.ANNUAL}
          accessUntilFormatted={accessUntilFormatted}
          locale={locale}
          tier={tier}
        />
      ) : null}
      reasonSelect={showInlineReasonSelect ? reasonSelect : null}
      isFeeFreeFlow={isFeeFreeFlow}
      plansRestructureEnabled={plansRestructureEnabled}
      locale={locale}
      tier={tier}
    />
  )
}

CancelSubscriptionModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onSubmit: func.isRequired,
  onReasonCapture: func,
  subscriptionId: number.isRequired,
  trackingData: trackingDataShape,
  feeData: shape({
    cancel_fee: string,
    access_until: string,
    remaining_months: number,
    monthly_rate: string,
    total_remaining: string,
  }),
  feeError: shape({
    message: string,
  }),
  isFeeLoading: bool,
  locale: string,
  subscriptionV16Enabled: bool,
  planType: string,
  gracePeriodEligible: bool,
  commitmentReleased: bool,
  showPauseOffer: bool,
  oneMonthPause: bool,
  onPause: func,
  isPauseLoading: bool,
  tier: string,
  periodEndDate: string,
  isFeeFreeFlow: bool,
  plansRestructureEnabled: bool,
  cancelEndsNow: bool,
  accessUntilDate: string,
  billingTerm: string,
  planChanges: planChangesShape,
  essentialsPrice: number,
  currentPrice: number,
  premiumModelCount: string,
  premiumGenerations: number,
  downloadedCount: number,
  downloadedValue: number,
  upgradeUrl: string,
  onSchedule: func,
  isScheduling: bool,
}

export default CancelSubscriptionModal
